import { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/ui/empty-state';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * The fallback body, as a function component so it can reach i18n.
 *
 * The boundary itself has to be a class (React gives no hook equivalent of
 * `componentDidCatch`), and its fallback used to be a hand-built card that
 * reproduced the shared empty state almost line for line: the same 48px tinted
 * circle, the same `text-lg` heading, the same outline "Try Again". It carried
 * the app's last three untranslated user-facing strings with it. Every string
 * here keeps a `defaultValue` so a boundary that fired *because* i18n failed
 * still says something readable.
 */
function ErrorBoundaryFallback({ message, onRetry }: { message?: string; onRetry: () => void }) {
  const { t } = useTranslation('common');
  return (
    <EmptyState
      kind="unavailable"
      title={t('states.error', { defaultValue: 'Something went wrong' })}
      description={message || t('states.errorBody', { defaultValue: 'An unexpected error occurred.' })}
      action={{ label: t('actions.tryAgain', { defaultValue: 'Try again' }), onClick: onRetry }}
    />
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <ErrorBoundaryFallback message={this.state.error?.message} onRetry={this.handleRetry} />
      );
    }

    return this.props.children;
  }
}
